import React, { useState, useEffect } from 'react';

export type ToastType = 'info' | 'error' | 'success';

interface ToastProps {
  message?: string;
  type?: ToastType;
  duration?: number;
}

// Create a global toast function
let showToastFn: (message: string, type: ToastType, duration?: number) => void = () => {};

export const showToast = (message: string, type: ToastType = 'info', duration?: number) => {
  showToastFn(message, type, duration);
};

const Toast: React.FC<ToastProps> = ({ 
  message: initialMessage, 
  type: initialType = 'info', 
  duration: initialDuration = 3000 
}) => {
  const [visible, setVisible] = useState(!!initialMessage);
  const [message, setMessage] = useState(initialMessage || '');
  const [type, setType] = useState<ToastType>(initialType);
  const [duration, setDuration] = useState(initialDuration);
  
  // Set the showToast function ref to our local function
  useEffect(() => {
    showToastFn = (msg, typ, dur) => {
      setMessage(msg);
      setType(typ || 'info');
      setDuration(dur || 3000);
      setVisible(true);
    };
  }, []);
  
  // Hide toast after duration
  useEffect(() => {
    if (visible && message) {
      const timer = setTimeout(() => {
        setVisible(false);
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [visible, message, duration]);
  
  if (!visible) return null;
  
  const toastClasses = {
    info: 'bg-blue-50 border-blue-300 text-blue-800',
    error: 'bg-red-50 border-red-300 text-red-800',
    success: 'bg-green-50 border-green-300 text-green-800'
  };
  
  return (
    <div 
      className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-md shadow-md border ${toastClasses[type]} flex items-center`}
      role="alert"
    >
      <div className="mr-3">
        {type === 'info' && (
          <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        )}
        {type === 'error' && (
          <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )}
        {type === 'success' && (
          <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      <div>
        <p className="font-medium">{message}</p>
      </div>
      <button 
        className="ml-auto text-gray-400 hover:text-gray-600"
        onClick={() => setVisible(false)}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default Toast;